import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { eventApi, businessApi } from '../services/api';
import { Event, Business } from '../types';
import { Modal } from '../components/Modal';

const eventSchema = z.object({
  businessId: z.string().min(1, 'Business is required'),
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  startDate: z.string().min(1, 'Start date is required'),
  endDate: z.string().min(1, 'End date is required'),
  location: z.string().optional(),
  maxParticipants: z.number().min(1).optional(),
  price: z.number().min(0).optional(),
  isPublic: z.boolean().default(true),
});

type EventForm = z.infer<typeof eventSchema>;

export function Events() {
  const [searchParams] = useSearchParams();
  const initialBusinessId = searchParams.get('businessId') || '';
  
  const [events, setEvents] = useState<Event[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<EventForm>({
    resolver: zodResolver(eventSchema),
    defaultValues: {
      businessId: initialBusinessId,
      isPublic: true,
      maxParticipants: 20,
      price: 0,
    },
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const businessesRes = await businessApi.getMyBusinesses();
      const businesses = businessesRes.data.data.businesses || [];
      setBusinesses(businesses);

      if (businesses.length > 0) {
        const eventsRes = await eventApi.getBusinessEvents(businesses[0].id);
        setEvents(eventsRes.data.data.events || []);
      } else {
        setEvents([]);
      }
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: EventForm) => {
    try {
      if (editingEvent) {
        await eventApi.updateEvent(editingEvent.id, data as any);
        toast.success('Event updated successfully');
      } else {
        await eventApi.createEvent(data as any);
        toast.success('Event created successfully');
      }
      setIsModalOpen(false);
      setEditingEvent(null);
      reset();
      fetchData();
    } catch (error) {
      toast.error(editingEvent ? 'Failed to update event' : 'Failed to create event');
    }
  };

  const handleEdit = (event: Event) => {
    setEditingEvent(event);
    reset({
      businessId: event.businessId,
      title: event.title,
      description: event.description || '',
      startDate: new Date(event.startDate).toISOString().slice(0, 16),
      endDate: new Date(event.endDate).toISOString().slice(0, 16),
      location: event.location || '',
      maxParticipants: event.maxParticipants || 20,
      price: event.price || 0,
      isPublic: event.isPublic,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await eventApi.deleteEvent(id);
      toast.success('Event deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleAddNew = () => {
    setEditingEvent(null);
    reset({
      businessId: businesses[0]?.id || '',
      title: '',
      description: '',
      startDate: '',
      endDate: '',
      location: '',
      maxParticipants: 20,
      price: 0,
      isPublic: true,
    });
    setIsModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-secondary-900">Events</h1>
        <button onClick={handleAddNew} className="btn-primary">
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Event
        </button>
      </div>

      {events.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((event) => (
            <div key={event.id} className="card p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-secondary-900">{event.title}</h3>
                  <p className="text-sm text-secondary-500">{event.business?.name}</p>
                </div>
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    event.isPublic
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {event.isPublic ? 'Public' : 'Private'}
                </span>
              </div>

              <p className="mt-3 text-sm text-secondary-600 line-clamp-2">
                {event.description || 'No description'}
              </p>

              <div className="mt-4 space-y-2 text-sm text-secondary-600">
                <p>
                  <span className="font-medium">Start:</span>{' '}
                  {new Date(event.startDate).toLocaleString()}
                </p>
                <p>
                  <span className="font-medium">End:</span>{' '}
                  {new Date(event.endDate).toLocaleString()}
                </p>
                {event.location && (
                  <p>
                    <span className="font-medium">Location:</span> {event.location}
                  </p>
                )}
                {event.price !== undefined && event.price > 0 && (
                  <p>
                    <span className="font-medium">Price:</span> ${event.price}
                  </p>
                )}
              </div>

              <div className="mt-6 flex space-x-2">
                <button
                  onClick={() => handleEdit(event)}
                  className="flex-1 btn-secondary text-sm"
                >
                  <PencilIcon className="h-4 w-4 mr-1 inline" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(event.id)}
                  className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card p-12 text-center">
          <p className="text-secondary-500 mb-4">No events yet.</p>
          <button onClick={handleAddNew} className="btn-primary">
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Your First Event
          </button>
        </div>
      )}

      {/* Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingEvent(null);
        }}
        title={editingEvent ? 'Edit Event' : 'Add New Event'}
        maxWidth="lg"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {!editingEvent && (
            <div>
              <label className="label">Business *</label>
              <select {...register('businessId')} className="input">
                {businesses.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
              {errors.businessId && (
                <p className="text-sm text-red-600 mt-1">{errors.businessId.message}</p>
              )}
            </div>
          )}

          <div>
            <label className="label">Event Title *</label>
            <input {...register('title')} type="text" className="input" />
            {errors.title && <p className="text-sm text-red-600 mt-1">{errors.title.message}</p>}
          </div>

          <div>
            <label className="label">Description</label>
            <textarea {...register('description')} className="input" rows={3} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Start Date *</label>
              <input {...register('startDate')} type="datetime-local" className="input" />
              {errors.startDate && (
                <p className="text-sm text-red-600 mt-1">{errors.startDate.message}</p>
              )}
            </div>
            <div>
              <label className="label">End Date *</label>
              <input {...register('endDate')} type="datetime-local" className="input" />
              {errors.endDate && (
                <p className="text-sm text-red-600 mt-1">{errors.endDate.message}</p>
              )}
            </div>
          </div>

          <div>
            <label className="label">Location</label>
            <input {...register('location')} type="text" className="input" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Max Participants</label>
              <input
                {...register('maxParticipants', { valueAsNumber: true })}
                type="number"
                min={1}
                className="input"
              />
            </div>
            <div>
              <label className="label">Price ($)</label>
              <input
                {...register('price', { valueAsNumber: true })}
                type="number"
                min={0}
                step="0.01"
                className="input"
              />
            </div>
          </div>

          <div className="flex items-center">
            <input {...register('isPublic')} type="checkbox" className="h-4 w-4 rounded border-secondary-300" />
            <label className="ml-2 text-sm text-secondary-700">Public event</label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setIsModalOpen(false);
                setEditingEvent(null);
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="btn-primary">
              {isSubmitting ? 'Saving...' : editingEvent ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
